import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { FormattedDate } from "react-intl";
import { Dialog } from "@syos-music/common";
import {
  SwitchField,
  TextField,
  SelectField,
  CheckboxGroupField,
  Fieldset,
} from "@syos-music/form";

import { Button, Grid, Typography, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import FileField from "components/form/FileField";
import IntlTextField from "components/form/IntlTextField";
import IntlUrlField from "components/form/IntlUrlField";

const QUERY = gql`
  query {
    articleSections {
      edges {
        node {
          id
          name
          label {
            fr
          }
        }
      }
    }
  }
`;

export default function ArticleMetadata({ article, children }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const [updateArticle] = useMutation(MUTATION);

  const { data } = useQuery(QUERY, {
    skip: article.type !== "blogPost",
  });

  const getArticleSection = React.useCallback(
    (articleSectionId) =>
      data?.articleSections.edges.find(
        ({ node }) => node.id === articleSectionId
      )?.node,
    [data]
  );

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog maxWidth="lg" open={isOpen} onClose={close}>
        <Form
          onSubmit={(values) => {
            const teaserImage = values.teaserImage
              ? {
                  path: values.teaserImage.path,
                  isPublic: values.teaserImage.isPublic,
                  metas: values.teaserImage.metas,
                }
              : null;

            const bannerImage = values.bannerImage
              ? {
                  path: values.bannerImage.path,
                  isPublic: values.bannerImage.isPublic,
                  metas: values.bannerImage.metas,
                }
              : null;

            const metadataImage = values.metadata?.image
              ? {
                  path: values.metadata.image.path,
                  isPublic: values.metadata.image.isPublic,
                }
              : null;

            return updateArticle({
              variables: {
                articleId: article.id,
                ...values,
                teaserImage,
                bannerImage,
                metadata: {
                  ...values.metadata,
                  image: metadataImage,
                },
              },
            })
              .then(() => {})
              .catch(() => true);
          }}
          initialValues={{
            published: article.published,
            url: article.url,
            title: article.title,
            teaser: article.teaser,
            teaserImage: article.teaserImage,
            bannerImage: article.bannerImage,
            metadata: article.metadata,

            // Blog post
            section:
              article.type === "blogPost" ? article.section?.id : undefined,
            pinned: article.pinned,
            subscribersOnly: article.subscribersOnly,

            // Artist
            artistName:
              article.type === "artist" ? article.artistName : undefined,
            instruments:
              article.type === "artist" ? article.instruments : undefined,
            bandName: article.type === "artist" ? article.bandName : undefined,
            bandsInTownId:
              article.type === "artist" ? article.bandsInTownId : undefined,
            quote: article.type === "artist" ? article.quote : undefined,
            social: article.type === "artist" ? article.social : undefined,
            webJpEnabled:
              article.type === "artist" ? article.webJpEnabled : undefined,
          }}
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, submitting, pristine, values, form }) => {
            let pathnamePrefix = "";
            switch (article.type) {
              case "artist":
                pathnamePrefix = "/artists/";
                break;
              case "job":
                pathnamePrefix = "/jobs/";
                break;

              case "blogPost": {
                const articleSection = getArticleSection(values.section);
                const sectionName = articleSection?.name ?? "_NO_BLOG_SECTION_";
                pathnamePrefix = `/blog/${sectionName}/`;
                break;
              }

              case "page":
              default:
                break;
            }

            return (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title>Informations & métadonnées</Dialog.Title>

                <Dialog.Content>
                  <Grid container justifyContent="flex-end">
                    <Grid item xs={12} md={article.type === "artist" ? 6 : 12}>
                      <SwitchField
                        name="published"
                        label="Publié"
                        helperText={
                          article.published && (
                            <span>
                              (Date de première publication&nbsp;:{" "}
                              <FormattedDate
                                value={article.publishedAt}
                                year="numeric"
                                month="long"
                                day="2-digit"
                                hour="2-digit"
                                minute="2-digit"
                              />
                              )
                            </span>
                          )
                        }
                      />
                    </Grid>

                    {article.type === "artist" && (
                      <Grid item xs={12} md={6}>
                        <SwitchField
                          name="webJpEnabled"
                          label="Visible sur syos.jp"
                        />
                      </Grid>
                    )}

                    {article.type === "blogPost" && (
                      <>
                        <Grid item xs={12} md={4}>
                          <SelectField name="section" label="Rubrique" required>
                            {data?.articleSections.edges.map(({ node }) => (
                              <MenuItem key={node.id} value={node.id}>
                                {node.label.fr}
                              </MenuItem>
                            ))}
                          </SelectField>

                          <OnChange name="section">
                            {(sectionId) => {
                              const newSection = getArticleSection(sectionId);

                              const re = new RegExp(`/blog/.*?/`);
                              const prefix = `/blog/${newSection.name}/`;

                              form.change("url", {
                                fr: values.url?.fr?.replace(re, prefix),
                                en: values.url?.en?.replace(re, prefix),
                              });
                            }}
                          </OnChange>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <SwitchField
                            name="pinned"
                            label="Épinglé"
                            helperText="Apparaîtra toujours avant les autres articles dans les listes"
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <SwitchField
                            name="subscribersOnly"
                            label="Abonnés uniquement"
                            helperText="Le contenu intégral ne sera pas visible par les utilisateurs non connectés"
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <IntlUrlField
                        name="url"
                        pathnamePrefix={pathnamePrefix}
                        required
                        label="URL"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField name="title.fr" label="🇫🇷 Titre" />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField name="title.en" label="🇬🇧 Titre" />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField name="teaser.fr" label="🇫🇷 Résumé" multiline />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField name="teaser.en" label="🇬🇧 Résumé" multiline />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileField
                        name="teaserImage"
                        uploadPath={`articles/${article.id}`}
                        isPublic
                        preview="image"
                        label="Image de résumé"
                        helperText="Visible dans les listes (blog, artistes, …)"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FileField
                        name="bannerImage"
                        uploadPath={`articles/${article.id}`}
                        isPublic
                        preview="image"
                        label="Banière"
                      />
                    </Grid>

                    {article.type === "artist" && (
                      <Grid item xs={12}>
                        <Fieldset legend="Artiste">
                          <Grid container>
                            <Grid item xs={12} md={4}>
                              <TextField
                                name="artistName"
                                required
                                label="Nom de l’artiste"
                              />
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <TextField
                                name="bandName"
                                label="Nom du groupe"
                              />
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <TextField
                                name="bandsInTownId"
                                label="ID bandsintown.com"
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <CheckboxGroupField
                                name="instruments"
                                label="Instruments"
                              >
                                <CheckboxGroupField.Option
                                  name="saxophone"
                                  label="Saxophone"
                                />
                                <CheckboxGroupField.Option
                                  name="clarinet"
                                  label="Clarinette"
                                />
                              </CheckboxGroupField>
                            </Grid>

                            <Grid item xs={12}>
                              <IntlTextField
                                name="quote"
                                label="Citation"
                                multiline
                                locales={
                                  values.webJpEnabled
                                    ? ["en", "fr", "ja"]
                                    : ["en", "fr"]
                                }
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="h3" color="primary">
                                Social
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="social.facebook"
                                label="Facebook"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="social.instagram"
                                label="Instagram"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                name="social.youtube"
                                label="YouTube"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField name="social.tiktok" label="TikTok" />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField name="social.web" label="Web" />
                            </Grid>
                          </Grid>
                        </Fieldset>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Fieldset legend="Metadonnées">
                        <Grid container>
                          <Grid item xs={12}>
                            <IntlTextField
                              name="metadata.title"
                              label="Titre"
                              maxLength={70}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <IntlTextField
                              name="metadata.description"
                              label="Description"
                              multiline
                              minLength={100}
                              maxLength={155}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <SwitchField
                              name="metadata.noIndex"
                              label="Désactiver l’indexation dans les moteurs de recherche"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <FileField
                              name="metadata.image"
                              uploadPath={`articles/${article.id}`}
                              isPublic
                              preview="image"
                              label="Image de partage"
                              helperText="Facebook, Twitter, …"
                            />
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Grid>
                  </Grid>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton
                    type="submit"
                    loading={submitting}
                    disabled={pristine}
                  >
                    Enregistrer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
}

ArticleMetadata.fragments = {
  article: gql`
    fragment Articles_ArticleMetadata_Article on Article {
      id
      type
      published
      publishedAt

      url {
        fr
        en
      }

      title {
        fr
        en
      }
      teaser {
        fr
        en
      }
      teaserImage {
        id
        name
        url
        path
        isPublic

        metas {
          alt {
            fr
            en
          }
          legend {
            fr
            en
          }
        }
      }
      bannerImage {
        id
        name
        url
        path
        isPublic

        metas {
          alt {
            fr
            en
          }
          legend {
            fr
            en
          }
        }
      }

      metadata {
        title {
          fr
          en
        }
        description {
          fr
          en
        }
        image {
          id
          name
          url
          path
          isPublic
        }
        noIndex
      }

      ... on Article_BlogPost {
        section {
          id
          name
        }
        pinned
        subscribersOnly
      }

      ... on Article_Artist {
        artistName
        instruments
        bandName
        bandsInTownId
        quote {
          en
          fr
          ja
        }
        social {
          facebook
          instagram
          youtube
          tiktok
          web
        }
        webJpEnabled
      }
    }
  `,
};

const MUTATION = gql`
  mutation (
    $articleId: ID!
    $published: Boolean!
    $section: ID
    $pinned: Boolean
    $subscribersOnly: Boolean
    $url: IntlStringInput!
    $title: IntlStringInput
    $teaser: IntlStringInput
    $teaserImage: FileInput
    $bannerImage: FileInput
    $artistName: String
    $instruments: [String]
    $bandName: String
    $bandsInTownId: String
    $quote: IntlStringInput
    $social: UpdateArticle_Artist_Social
    $webJpEnabled: Boolean
    $metadata: ArticleMetadataInput
  ) {
    UpdateArticle(
      id: $articleId
      published: $published
      section: $section
      pinned: $pinned
      subscribersOnly: $subscribersOnly
      url: $url
      title: $title
      teaser: $teaser
      teaserImage: $teaserImage
      bannerImage: $bannerImage
      artistName: $artistName
      instruments: $instruments
      bandName: $bandName
      bandsInTownId: $bandsInTownId
      quote: $quote
      social: $social
      webJpEnabled: $webJpEnabled
      metadata: $metadata
    ) {
      article {
        id

        ...Articles_ArticleMetadata_Article
      }
    }
  }

  ${ArticleMetadata.fragments.article}
`;
